import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import Select from "react-select";
import { Bar, HorizontalBar, Line } from "react-chartjs-2";
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Col,
  Row,
  Table,
  Button,
  Input,
  FormGroup,
  Label,
} from "reactstrap";
import { ClipLoader } from "react-spinners";

import fileDownload from "js-file-download";

import api from "services/api";
import { useCompanies } from "hooks/companies";
import { useToast } from "../../../../hooks/toast";
import { formatDateLabel } from "../../../../utils/dateOnly";
import SpecificEmpresaRequiredNotice from "../../../../components/SpecificEmpresaRequiredNotice";
import {
  AUTOPILOT_COLORS,
  autopilotBodyStyle,
  autopilotCardStyle,
  autopilotDescriptionStyle,
  autopilotEyebrowStyle,
  autopilotHeaderStyle,
  autopilotInsetCardStyle,
  autopilotSecondaryButtonStyle,
  autopilotTitleStyle,
} from "../../../../components/autopilot/styles";
import DetalheEmissaoModal from "./DetalheEmissaoModal";
import gerarRelatorioEmissaoPdf from "./relatorioPdf";
import { gerarRelatorioEsgPdf } from "./relatorioEsgPeriodo";
import { baixarCardPng } from "./shareableCard";
import {
  calcularEquivalencias,
  notaCobertura,
  EQUIVALENCIA_LABELS,
} from "./equivalencias";
import "./styles.css";

const COLORS = [
  "#1d8cf8",
  "#00bf9a",
  "#fd5d93",
  "#ff8d72",
  "#e14eca",
  "#f4d03f",
  "#5e72e4",
  "#11cdef",
  "#44d3b6",
  "#fca311",
];

const QUICK_RANGES = [
  { key: "month", label: "Mês atual", days: null },
  { key: "7d", label: "7 dias", days: 6 },
  { key: "30d", label: "30 dias", days: 29 },
  { key: "90d", label: "90 dias", days: 89 },
];

const DISTANCE_SOURCE_META = {
  cache: { label: "Cache", tone: "info" },
  ors: { label: "ORS", tone: "success" },
  haversine: { label: "Haversine", tone: "warning" },
  pendente_provedor: { label: "Pendente", tone: "warning" },
  sem_origem_destino: { label: "Sem origem/destino", tone: "danger" },
  sem_peso: { label: "Sem peso", tone: "danger" },
  sem_fator_modal: { label: "Sem fator modal", tone: "danger" },
  erro: { label: "Erro", tone: "danger" },
  nao_processado: { label: "Não processado", tone: "muted" },
};

const selectStyles = {
  menu: (base) => ({
    ...base,
    zIndex: 30,
    background: "var(--everlog-card-surface-flat)",
    border: "1px solid var(--everlog-card-border)",
    borderRadius: 14,
    boxShadow: "var(--everlog-card-shadow)",
  }),
  menuPortal: (base) => ({ ...base, zIndex: 50 }),
  control: (base, state) => ({
    ...base,
    background: "var(--everlog-card-surface-field)",
    borderColor: state.isFocused
      ? "var(--theme-accent)"
      : "var(--everlog-card-border)",
    borderRadius: 14,
    boxShadow: state.isFocused ? "0 0 0 3px var(--theme-accent-glow)" : "none",
    minHeight: 42,
    transition: "border-color 0.18s ease, box-shadow 0.18s ease",
  }),
  singleValue: (base) => ({
    ...base,
    color: "var(--everlog-card-title)",
  }),
  input: (base) => ({
    ...base,
    color: "var(--everlog-card-text)",
  }),
  placeholder: (base) => ({
    ...base,
    color: "var(--everlog-card-muted)",
  }),
  option: (base, state) => ({
    ...base,
    background: state.isFocused
      ? "var(--everlog-card-interactive-bg)"
      : state.isSelected
        ? "var(--everlog-card-surface-active)"
        : "var(--everlog-card-surface-flat)",
    color: "var(--everlog-card-text)",
    cursor: "pointer",
  }),
  dropdownIndicator: (base) => ({
    ...base,
    color: "var(--everlog-card-muted)",
  }),
  clearIndicator: (base) => ({
    ...base,
    color: "var(--everlog-card-muted)",
  }),
  indicatorSeparator: (base) => ({
    ...base,
    backgroundColor: "var(--everlog-card-border)",
  }),
};

// Estilo inline do botão de ação "Ver" na tabela. Inline vence o tema do
// black-dashboard (que pinta .btn-secondary com fundo próprio), garantindo o
// tom accent translúcido.
const ROW_ACTION_BUTTON_STYLE = {
  minWidth: 0,
  padding: "5px 12px",
  borderRadius: 8,
  fontWeight: 700,
  border: "1px solid color-mix(in srgb, var(--theme-accent) 45%, transparent)",
  background: "color-mix(in srgb, var(--theme-accent) 16%, transparent)",
  color: "var(--theme-accent)",
  boxShadow: "none",
};

const OPERACAO_PAGE_SIZES = [10, 25, 50];

const createDefaultFilters = () => ({
  // Abre em 90 dias (alinha com o quick range "90d" = subtract(89)), que traz
  // mais dados nos gráficos. Só varre mais linhas na agregação SQL já cacheada;
  // sem custo extra de IA/API externa (emissão vem pré-calculada).
  desde: moment().subtract(89, "days").format("YYYY-MM-DD"),
  ate: moment().format("YYYY-MM-DD"),
  modal: null,
  ufOrigem: null,
  ufDestino: null,
});

const getQuickRangeDates = (rangeKey) => {
  const today = moment();

  if (rangeKey === "month") {
    return {
      desde: today.clone().startOf("month").format("YYYY-MM-DD"),
      ate: today.format("YYYY-MM-DD"),
    };
  }

  const range = QUICK_RANGES.find((item) => item.key === rangeKey);
  const days = range?.days ?? 29;

  return {
    desde: today.clone().subtract(days, "days").format("YYYY-MM-DD"),
    ate: today.format("YYYY-MM-DD"),
  };
};

const formatNumber = (n, frac = 2) =>
  Number(n || 0).toLocaleString("pt-BR", {
    minimumFractionDigits: frac,
    maximumFractionDigits: frac,
  });

const formatMaybeNumber = (n, frac = 2) => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) {
    return "-";
  }

  return formatNumber(Number(n), frac);
};

const formatDate = (iso) => formatDateLabel(iso, "-");

const formatLocation = (uf, cidade) =>
  [uf, cidade].filter(Boolean).join(" - ") || "-";

const normalizeError = (error, fallback) =>
  (error &&
    error.response &&
    error.response.data &&
    error.response.data.error) ||
  error?.message ||
  fallback;

const getValidationError = (filters) => {
  if (!filters?.desde || !filters?.ate) {
    return "Informe o intervalo completo para consultar.";
  }

  const desde = moment(filters.desde, "YYYY-MM-DD", true);
  const ate = moment(filters.ate, "YYYY-MM-DD", true);

  if (!desde.isValid() || !ate.isValid()) {
    return "As datas informadas são inválidas.";
  }

  if (ate.isBefore(desde)) {
    return "A data final precisa ser igual ou posterior à inicial.";
  }

  return null;
};

const getDistanceSourceMeta = (value) =>
  DISTANCE_SOURCE_META[value] || {
    label: value || "-",
    tone: "muted",
  };

const buildChartOptions = ({
  metricLabel,
  decimals = 2,
  horizontal = false,
  fill = false,
}) => ({
  maintainAspectRatio: false,
  legend: { display: false },
  elements: {
    line: {
      tension: 0.25,
      fill,
    },
  },
  tooltips: {
    callbacks: {
      label: (tooltipItem) => {
        const rawValue = horizontal ? tooltipItem.xLabel : tooltipItem.yLabel;
        return `${metricLabel}: ${formatNumber(rawValue, decimals)}`;
      },
    },
  },
  scales: horizontal
    ? {
        xAxes: [
          {
            ticks: {
              fontColor: "rgba(255,255,255,0.65)",
              callback: (value) => formatNumber(value, decimals),
            },
            gridLines: {
              color: "rgba(255,255,255,0.05)",
              zeroLineColor: "rgba(255,255,255,0.08)",
            },
          },
        ],
        yAxes: [
          {
            ticks: {
              fontColor: "#f5f7fb",
            },
            gridLines: {
              display: false,
            },
          },
        ],
      }
    : {
        xAxes: [
          {
            ticks: {
              fontColor: "rgba(255,255,255,0.65)",
            },
            gridLines: {
              display: false,
            },
          },
        ],
        yAxes: [
          {
            ticks: {
              fontColor: "rgba(255,255,255,0.65)",
              callback: (value) => formatNumber(value, decimals),
            },
            gridLines: {
              color: "rgba(255,255,255,0.05)",
              zeroLineColor: "rgba(255,255,255,0.08)",
            },
          },
        ],
      },
});

const SummaryMetricCard = ({ label, tag, value, helper, iconClassName, tone = "info" }) => (
  <div
    className={`emissoes-summary-metric emissoes-summary-metric--${tone}`}
    style={{
      ...autopilotInsetCardStyle,
      border:
        "1px solid color-mix(in srgb, var(--emissoes-tone) 38%, var(--everlog-card-border))",
      padding: "12px 14px",
      background: AUTOPILOT_COLORS.insetSurface,
    }}
  >
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        gap: 12,
      }}
    >
      <span
        className="emissoes-summary-metric__label"
        style={{
          fontSize: "0.72rem",
          fontWeight: 700,
          letterSpacing: "0.05em",
          textTransform: "uppercase",
        }}
      >
        {label}
      </span>
      {tag ? (
        <span
          style={{
            color: AUTOPILOT_COLORS.muted,
            fontSize: "0.75rem",
            fontWeight: 600,
            flexShrink: 0,
          }}
        >
          {tag}
        </span>
      ) : null}
    </div>

    <div
      style={{
        marginTop: 8,
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "space-between",
        gap: 12,
      }}
    >
      <div
        style={{
          color: AUTOPILOT_COLORS.title,
          fontSize: "1.5rem",
          fontWeight: 800,
          lineHeight: 1,
        }}
      >
        {value}
      </div>
      {iconClassName ? (
        <div className={`emissoes-summary-metric__icon emissoes-summary-metric__icon--${tone}`}>
          <i className={iconClassName} />
        </div>
      ) : null}
    </div>

    {helper ? (
      <div
        style={{
          marginTop: 7,
          color: "#d9e8ff",
          fontSize: "0.78rem",
          fontWeight: 600,
          letterSpacing: "0.02em",
          lineHeight: 1.45,
        }}
      >
        {helper}
      </div>
    ) : null}
  </div>
);

const SummaryOverviewCard = ({ kpis }) => {
  const metrics = [
    {
      key: "total",
      label: "Emissão total",
      tag: "CO₂",
      value: `${formatNumber(kpis.totalToneladasCO2, 3)} t`,
      helper: `${formatNumber(kpis.totalKgCO2, 2)} kg CO₂ calculados no período`,
      iconClassName: "tim-icons icon-planet",
      tone: "info",
    },
    {
      key: "ctes",
      label: "CTes considerados",
      tag: "Docs",
      value: formatNumber(kpis.qtdCtes, 0),
      helper: "Documentos com cálculo válido dentro do recorte atual",
      iconClassName: "tim-icons icon-paper",
      tone: "success",
    },
    {
      key: "distancia",
      label: "Distância total",
      tag: "KM",
      value: formatNumber(kpis.distanciaTotalKm, 0),
      helper: "Soma das rotas usadas no cálculo das emissões",
      iconClassName: "tim-icons icon-map-big",
      tone: "warning",
    },
    {
      key: "media",
      label: "Emissão média por CTe",
      tag: "kg CO₂",
      value: formatNumber(kpis.emissaoMediaKgCO2, 2),
      helper: "Média de emissão por documento calculado",
      iconClassName: "tim-icons icon-chart-bar-32",
      tone: "pink",
    },
  ];

  return (
    <Card
      className="card-everlog-shell emissoes-summary-card"
      style={{
        ...autopilotCardStyle,
        display: "flex",
        flexDirection: "column",
        background: "var(--everlog-card-hero-bg)",
        marginBottom: "1.5rem",
      }}
    >
      <CardHeader style={autopilotHeaderStyle}>
        <div>
          <div style={autopilotEyebrowStyle}>Sustentabilidade</div>
          <div
            style={{
              ...autopilotTitleStyle,
              marginBottom: "0.3rem",
            }}
          >
            Resumo do período
          </div>
          <p
            style={{
              ...autopilotDescriptionStyle,
              maxWidth: 760,
            }}
          >
            Indicadores-chave do recorte atual, organizados no mesmo padrão visual do
            Autopilot.
          </p>
        </div>
      </CardHeader>

      <CardBody
        style={{
          ...autopilotBodyStyle,
          display: "flex",
          flexDirection: "column",
          gap: "12px",
        }}
      >
        <div className="emissoes-summary-grid">
          {metrics.map((metric) => (
            <SummaryMetricCard key={metric.key} {...metric} />
          ))}
        </div>
      </CardBody>
    </Card>
  );
};

const EmptyPanel = ({ title, description, actions }) => (
  <Card className="card-everlog-shell emissoes-empty-card">
    <CardBody>
      <div className="emissoes-state-panel">
        <div className="emissoes-empty-card__eyebrow">Sem resultados</div>
        <h4>{title}</h4>
        <p>{description}</p>
        {actions ? <div className="emissoes-empty-card__actions">{actions}</div> : null}
      </div>
    </CardBody>
  </Card>
);

const SectionCard = ({ eyebrow, title, description, actions, children }) => (
  <Card
    className="card-everlog-shell emissoes-section-card"
    style={{
      ...autopilotCardStyle,
      display: "flex",
      flexDirection: "column",
      background: "var(--everlog-card-hero-bg)",
      marginBottom: "1.5rem",
    }}
  >
    <CardHeader
      className={`emissoes-section-card__header ${actions ? "emissoes-section-card__header--split" : ""}`}
    >
      <div>
        {eyebrow ? <div style={autopilotEyebrowStyle}>{eyebrow}</div> : null}
        <div style={{ ...autopilotTitleStyle, marginBottom: "0.3rem" }}>{title}</div>
        {description ? (
          <p style={{ ...autopilotDescriptionStyle, maxWidth: 760, marginBottom: 0 }}>
            {description}
          </p>
        ) : null}
      </div>
      {actions ? <div className="emissoes-section-card__actions">{actions}</div> : null}
    </CardHeader>
    <CardBody className="emissoes-section-card__body">{children}</CardBody>
  </Card>
);

const InsetPanel = ({ title, description, bodyClassName = "", children }) => (
  <div className="emissoes-inset-panel">
    {title || description ? (
      <div className="emissoes-inset-panel__header">
        <div>
          {title ? (
            <CardTitle tag="h6" className="mb-1">
              {title}
            </CardTitle>
          ) : null}
          {description ? <small className="emissoes-muted-text">{description}</small> : null}
        </div>
      </div>
    ) : null}
    <div className={`emissoes-inset-panel__body ${bodyClassName}`.trim()}>{children}</div>
  </div>
);

const EmissoesCarbonoIndicador = () => {
  const {
    empresa,
    transportadora,
    hasEmpresaSelection,
  } = useCompanies();
  const { addToast } = useToast();

  const empresaId = empresa && empresa.value;
  const transportadoraId = transportadora && transportadora.value;

  const [filtros, setFiltros] = useState(createDefaultFilters);
  const filtrosRef = useRef(filtros);
  // Painel avançado colapsado por padrão; abre pelo botão "Filtros". O período
  // (pills) é o controle principal e fica sempre visível, espelhando o padrão
  // da Auditoria de Faturas.
  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  // Paginação client-side da tabela de Operação (o backend devolve o ranking
  // inteiro de uma vez; paginamos no front pra ter o footer estilo Faturas).
  const [operacaoPage, setOperacaoPage] = useState(1);
  const [operacaoPageSize, setOperacaoPageSize] = useState(OPERACAO_PAGE_SIZES[0]);
  // Explorador de CT-es (server-side): dados paginados, loading, busca aplicada e input.
  const [ctesData, setCtesData] = useState({ rows: [], total: 0 });
  const [ctesLoading, setCtesLoading] = useState(false);
  const [ctesBusca, setCtesBusca] = useState("");
  const [ctesBuscaInput, setCtesBuscaInput] = useState("");
  const detalheCacheRef = useRef({});
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [consultado, setConsultado] = useState(false);
  const [error, setError] = useState(null);
  const [pdfLoadingId, setPdfLoadingId] = useState(null);
  const [detailLoadingId, setDetailLoadingId] = useState(null);
  // Loadings dos artefatos ESG do período (relatório PDF, Excel, card PNG).
  const [relatorioEsgLoading, setRelatorioEsgLoading] = useState(false);
  const [excelLoading, setExcelLoading] = useState(false);
  const [cardEsgLoading, setCardEsgLoading] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [optionsError, setOptionsError] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [detailError, setDetailError] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [filterOptions, setFilterOptions] = useState({
    modais: [],
    estados: [],
  });

  useEffect(() => {
    filtrosRef.current = filtros;
  }, [filtros]);

  useEffect(() => {
    detalheCacheRef.current = {};
    setDetailModalOpen(false);
    setDetailData(null);
    setDetailError(null);
    setDetailLoading(false);
    setDetailLoadingId(null);
  }, [empresaId]);

  const validationError = useMemo(() => getValidationError(filtros), [filtros]);

  const buildParams = useCallback((activeFilters) => {
    const params = {
      idEmbarcador: empresaId,
      desde: activeFilters.desde,
      ate: activeFilters.ate,
    };

    if (transportadoraId) {
      params.idTransportadora = transportadoraId;
    }
    if (activeFilters.modal?.value) {
      params.idModal = activeFilters.modal.value;
    }
    if (activeFilters.ufOrigem?.value) {
      params.ufOrigem = activeFilters.ufOrigem.value;
    }
    if (activeFilters.ufDestino?.value) {
      params.ufDestino = activeFilters.ufDestino.value;
    }

    return params;
  }, [empresaId, transportadoraId]);

  useEffect(() => {
    let cancelled = false;

    async function loadOptions() {
      setLoadingOptions(true);
      setOptionsError(null);

      try {
        const [modaisRes, estadosRes] = await Promise.all([
          api.get("/frete/modais"),
          api.get("/frete/estados"),
        ]);

        if (cancelled) {
          return;
        }

        setFilterOptions({
          modais: (modaisRes.data || []).map((item) => ({
            value: item.id,
            label: item.nome,
          })),
          estados: (estadosRes.data || []).map((item) => ({
            value: item.uf,
            label: `${item.uf} - ${item.nome}`,
          })),
        });
      } catch (requestError) {
        if (!cancelled) {
          setOptionsError(
            normalizeError(requestError, "Não foi possível carregar os filtros auxiliares.")
          );
        }
      } finally {
        if (!cancelled) {
          setLoadingOptions(false);
        }
      }
    }

    loadOptions();

    return () => {
      cancelled = true;
    };
  }, []);

  const loadDashboard = useCallback(async (activeFilters) => {
    if (!empresaId || !hasEmpresaSelection || getValidationError(activeFilters)) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const res = await api.get("/indicadores/emissoes-carbono", {
        params: buildParams(activeFilters),
      });
      setData(res.data);
      setConsultado(true);
    } catch (requestError) {
      setError(
        normalizeError(requestError, "Erro ao carregar o indicador de emissões.")
      );
      setConsultado(true);
    } finally {
      setLoading(false);
    }
  }, [buildParams, empresaId, hasEmpresaSelection]);

  const consultar = useCallback(async () => {
    if (validationError) {
      return;
    }

    await loadDashboard(filtros);
  }, [filtros, loadDashboard, validationError]);

  useEffect(() => {
    if (!hasEmpresaSelection) {
      setData(null);
      setError(null);
      setConsultado(false);
      return;
    }

    loadDashboard(filtrosRef.current);
  }, [empresaId, hasEmpresaSelection, loadDashboard, transportadoraId]);

  // Relatório ESG do período em Excel (.xlsx) — gerado no backend (padrão Everlog).
  const baixarExcel = useCallback(async () => {
    if (!empresaId || !hasEmpresaSelection || validationError) {
      return;
    }
    setExcelLoading(true);
    try {
      const res = await api.get("/indicadores/emissoes-carbono/relatorio-xlsx", {
        params: buildParams(filtros),
        responseType: "blob",
      });
      fileDownload(res.data, `relatorio-esg-${filtros.desde}-a-${filtros.ate}.xlsx`);
    } catch (requestError) {
      addToast({
        place: "tr",
        type: "danger",
        title: "Erro ao gerar Excel",
        description: normalizeError(
          requestError,
          "Não foi possível gerar o Excel do período."
        ),
        icon: "tim-icons icon-alert-circle-exc",
      });
    } finally {
      setExcelLoading(false);
    }
  }, [addToast, buildParams, empresaId, filtros, hasEmpresaSelection, validationError]);

  // Explorador de CT-es: busca server-side paginada (nº do CTe ou transportadora).
  const loadCtes = useCallback(
    async (page, busca) => {
      if (!empresaId || !hasEmpresaSelection || validationError) {
        setCtesData({ rows: [], total: 0 });
        return;
      }
      setCtesLoading(true);
      try {
        const res = await api.get("/indicadores/emissoes-carbono/ctes", {
          params: {
            ...buildParams(filtrosRef.current),
            page,
            pageSize: operacaoPageSize,
            busca: busca || undefined,
          },
        });
        setCtesData({
          rows: Array.isArray(res.data && res.data.rows) ? res.data.rows : [],
          total: Number((res.data && res.data.total) || 0),
        });
      } catch (requestError) {
        setCtesData({ rows: [], total: 0 });
        addToast({
          place: "tr",
          type: "danger",
          title: "Erro ao carregar CT-es",
          description: normalizeError(
            requestError,
            "Não foi possível carregar a lista de CT-es."
          ),
          icon: "tim-icons icon-alert-circle-exc",
        });
      } finally {
        setCtesLoading(false);
      }
    },
    [addToast, buildParams, empresaId, hasEmpresaSelection, operacaoPageSize, validationError]
  );

  const aplicarBuscaCtes = useCallback(() => {
    setOperacaoPage(1);
    setCtesBusca(ctesBuscaInput.trim());
  }, [ctesBuscaInput]);

  const limparBuscaCtes = useCallback(() => {
    setCtesBuscaInput("");
    setOperacaoPage(1);
    setCtesBusca("");
  }, []);

  const fetchCteDetalhe = useCallback(async (idCte) => {
    if (!empresaId || !idCte) {
      return null;
    }

    if (detalheCacheRef.current[idCte]) {
      return detalheCacheRef.current[idCte];
    }

    const res = await api.get(`/indicadores/emissoes-carbono/cte/${idCte}`, {
      params: { idEmbarcador: empresaId },
    });

    if (!res.data) {
      throw new Error("Detalhe do CTe não encontrado.");
    }

    detalheCacheRef.current[idCte] = res.data;
    return res.data;
  }, [empresaId]);

  const abrirDetalheCte = useCallback(async (idCte) => {
    if (!empresaId || !idCte) return;

    setDetailModalOpen(true);
    setDetailLoading(true);
    setDetailError(null);
    setDetailData(null);
    setDetailLoadingId(idCte);

    try {
      const detalhe = await fetchCteDetalhe(idCte);
      setDetailData(detalhe);
    } catch (requestError) {
      setDetailError(
        normalizeError(
          requestError,
          "Não foi possível carregar os detalhes deste CTe."
        )
      );
    } finally {
      setDetailLoading(false);
      setDetailLoadingId(null);
    }
  }, [empresaId, fetchCteDetalhe]);

  const fecharDetalheCte = useCallback(() => {
    setDetailModalOpen(false);
  }, []);

  const baixarRelatorioPdf = useCallback(
    async (idCte) => {
      if (!empresaId || !idCte) return;

      setPdfLoadingId(idCte);

      try {
        const detalhe = await fetchCteDetalhe(idCte);
        gerarRelatorioEmissaoPdf(detalhe);
      } catch (requestError) {
        addToast({
          place: "tr",
          type: "danger",
          title: "Erro ao gerar relatório",
          description: normalizeError(
            requestError,
            "Não foi possível gerar o relatório em PDF deste CTe."
          ),
          icon: "tim-icons icon-alert-circle-exc",
        });
      } finally {
        setPdfLoadingId(null);
      }
    },
    [addToast, empresaId, fetchCteDetalhe]
  );

  const datasetModal = useMemo(() => {
    if (!data?.porModal?.length) {
      return null;
    }

    return {
      labels: data.porModal.map((row) => row.nomeModal),
      datasets: [
        {
          label: "t CO₂",
          data: data.porModal.map((row) => Number(row.totalKgCO2 || 0) / 1000),
          backgroundColor: COLORS,
          borderRadius: 8,
          maxBarThickness: 42,
        },
      ],
    };
  }, [data]);

  const datasetLinha = useMemo(() => {
    if (!data?.porMes?.length) {
      return null;
    }

    return {
      labels: data.porMes.map((row) => row.mes),
      datasets: [
        {
          label: "t CO₂ por mês",
          data: data.porMes.map((row) => Number(row.totalKgCO2 || 0) / 1000),
          borderColor: "#00bf9a",
          backgroundColor: "rgba(0,191,154,0.18)",
          fill: true,
          pointBackgroundColor: "#00bf9a",
          pointBorderColor: "#00bf9a",
          pointHoverRadius: 5,
        },
      ],
    };
  }, [data]);

  const datasetTransportadoras = useMemo(() => {
    if (!data?.topTransportadoras?.length) {
      return null;
    }

    const rows = [...data.topTransportadoras].reverse();

    return {
      labels: rows.map((row) => row.nomeTransportadora || "(sem nome)"),
      datasets: [
        {
          label: "t CO₂",
          data: rows.map((row) => Number(row.totalKgCO2 || 0) / 1000),
          backgroundColor: rows.map((_, index) => COLORS[index % COLORS.length]),
          borderRadius: 8,
          barThickness: 16,
        },
      ],
    };
  }, [data]);

  const hasResults = useMemo(() => {
    if (!data?.kpis) {
      return false;
    }

    return Number(data.kpis.qtdCtes || 0) > 0;
  }, [data]);

  const activeQuickRange = useMemo(() => {
    return (
      QUICK_RANGES.find((item) => {
        const range = getQuickRangeDates(item.key);
        return range.desde === filtros.desde && range.ate === filtros.ate;
      })?.key || null
    );
  }, [filtros.ate, filtros.desde]);

  // Pill de período aplica na hora: seta o range (preservando modal/UF atuais) e
  // já consulta. O loadDashboard valida internamente, então não precisa guardar.
  const aplicarQuickRange = useCallback(
    (rangeKey) => {
      const novo = { ...filtrosRef.current, ...getQuickRangeDates(rangeKey) };
      setFiltros(novo);
      loadDashboard(novo);
    },
    [loadDashboard]
  );

  // Badge do botão "Filtros": conta só os filtros que não são período
  // (modal + UF origem + UF destino). O range manual continua sendo "período".
  const filtrosAvancadosCount = useMemo(() => {
    let total = 0;
    if (filtros.modal) total += 1;
    if (filtros.ufOrigem) total += 1;
    if (filtros.ufDestino) total += 1;
    return total;
  }, [filtros.modal, filtros.ufOrigem, filtros.ufDestino]);

  // Período ativo sempre visível no principal, mesmo com o drawer fechado.
  const periodoAtivoLabel = useMemo(() => {
    if (!filtros.desde || !filtros.ate) {
      return "";
    }
    return `${moment(filtros.desde).format("DD/MM/YYYY")} a ${moment(
      filtros.ate
    ).format("DD/MM/YYYY")}`;
  }, [filtros.desde, filtros.ate]);

  // Contexto carimbado nos artefatos ESG (relatório PDF/CSV e card PNG).
  const contextoEsg = useMemo(() => {
    const partes = [];
    if (transportadora && transportadora.label) {
      partes.push(`Transportadora: ${transportadora.label}`);
    }
    if (filtros.modal) partes.push(`Modal: ${filtros.modal.label}`);
    if (filtros.ufOrigem) {
      partes.push(`UF origem: ${filtros.ufOrigem.label || filtros.ufOrigem.value}`);
    }
    if (filtros.ufDestino) {
      partes.push(`UF destino: ${filtros.ufDestino.label || filtros.ufDestino.value}`);
    }
    return {
      periodoLabel: periodoAtivoLabel,
      filtrosLabel: partes.join(" · "),
      desde: filtros.desde,
      ate: filtros.ate,
    };
  }, [
    filtros.desde,
    filtros.ate,
    filtros.modal,
    filtros.ufOrigem,
    filtros.ufDestino,
    transportadora,
    periodoAtivoLabel,
  ]);

  // Equivalências e nota de cobertura do recorte atual (Vitrine Verde).
  const equivalenciasEsg = useMemo(
    () => (data?.kpis ? calcularEquivalencias(data.kpis.totalToneladasCO2) : null),
    [data]
  );
  const notaCoberturaEsg = useMemo(() => notaCobertura(data?.cobertura), [data]);
  const coberturaPctEsg = useMemo(() => {
    const c = data?.cobertura;
    if (!c || !c.totalCtes) return null;
    return (Number(c.percentual) || 0).toLocaleString("pt-BR", {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    });
  }, [data]);

  // Relatório ESG consolidado do período — PDF (PO1-2401), client-side.
  const gerarRelatorioEsg = useCallback(() => {
    if (!data || !data.kpis) return;
    setRelatorioEsgLoading(true);
    try {
      gerarRelatorioEsgPdf(data, contextoEsg);
    } catch (requestError) {
      addToast({
        place: "tr",
        type: "danger",
        title: "Erro ao gerar relatório",
        description: normalizeError(
          requestError,
          "Não foi possível gerar o relatório ESG do período."
        ),
        icon: "tim-icons icon-alert-circle-exc",
      });
    } finally {
      setRelatorioEsgLoading(false);
    }
  }, [addToast, contextoEsg, data]);

  // Card compartilhável (PNG) do período (PO1-2402), desenhado em canvas.
  const baixarCardEsg = useCallback(async () => {
    if (!data || !data.kpis) return;
    setCardEsgLoading(true);
    try {
      await baixarCardPng(data, contextoEsg);
    } catch (requestError) {
      addToast({
        place: "tr",
        type: "danger",
        title: "Erro ao gerar card",
        description: normalizeError(
          requestError,
          "Não foi possível gerar o card compartilhável."
        ),
        icon: "tim-icons icon-alert-circle-exc",
      });
    } finally {
      setCardEsgLoading(false);
    }
  }, [addToast, contextoEsg, data]);

  // Explorador server-side: reseta página/busca ao trocar o recorte (data) e
  // recarrega quando recorte, página, tamanho de página (via loadCtes) ou busca mudam.
  useEffect(() => {
    setOperacaoPage(1);
    setCtesBusca("");
    setCtesBuscaInput("");
  }, [data]);

  useEffect(() => {
    loadCtes(operacaoPage, ctesBusca);
  }, [loadCtes, operacaoPage, ctesBusca, data]);

  const ctesTotalPaginas = Math.max(
    1,
    Math.ceil((Number(ctesData.total) || 0) / operacaoPageSize)
  );
  const operacaoPaginaAtual = Math.min(operacaoPage, ctesTotalPaginas);

  const tableSummary = ctesData.total
    ? ctesBusca
      ? `${formatNumber(ctesData.total, 0)} CTe(s) para "${ctesBusca}", ordenados por emissão (kg CO₂).`
      : "CT-es do período ordenados por emissão (kg CO₂)."
    : ctesLoading
    ? "Carregando CT-es..."
    : "Nenhum CTe para o recorte atual.";

  // Total desta página (tfoot). A soma do recorte inteiro está nos KPIs.
  const ctesPageTotais = useMemo(() => {
    return (ctesData.rows || []).reduce(
      (acc, row) => {
        acc.distanciaKm += Number(row.distanciaKm || 0);
        acc.pesoToneladas += Number(row.pesoToneladas || 0);
        acc.emissaoKgCO2 += Number(row.emissaoKgCO2 || 0);
        return acc;
      },
      { distanciaKm: 0, pesoToneladas: 0, emissaoKgCO2: 0 }
    );
  }, [ctesData]);

  const renderOperacaoPager = (label, targetPage, disabled) => (
    <Button
      size="sm"
      className={
        disabled
          ? "emissoes-pagination__btn"
          : "emissoes-button-primary emissoes-button-primary--sm"
      }
      disabled={disabled || ctesLoading}
      onClick={() => setOperacaoPage(targetPage)}
    >
      {label}
    </Button>
  );

  const resetarFiltros = useCallback(() => {
    const novo = createDefaultFilters();
    setFiltros(novo);
    loadDashboard(novo);
  }, [loadDashboard]);
  const selectMenuPortalTarget =
    typeof document !== "undefined" ? document.body : null;

  if (!hasEmpresaSelection) {
    return (
      <div className="content emissoes-page">
        <SpecificEmpresaRequiredNotice
          title="Selecione um embarcador no cabeçalho"
          message="Esta visão usa o embarcador e a transportadora selecionados no topo da página para montar o recorte do indicador."
        />
      </div>
    );
  }

  return (
    <div className="content emissoes-page">
      <Card className="card-everlog-shell card-everlog-hero emissoes-hero">
        <CardBody>
          <div className="emissoes-hero__top">
            <div className="emissoes-hero__intro">
              <span className="emissoes-hero__eyebrow">Indicador de Sustentabilidade</span>
              <h2 className="emissoes-hero__title">Emissões de carbono por CTe</h2>
              <p className="emissoes-hero__description">
                Acompanhe o impacto das operações com base em distância, peso e fator do modal.
              </p>
            </div>
          </div>
        </CardBody>
      </Card>

      <Card
        className="card-everlog-shell emissoes-filter-card"
        style={{
          ...autopilotCardStyle,
          display: "flex",
          flexDirection: "column",
          background: "var(--everlog-card-hero-bg)",
        }}
      >
        <CardHeader className="emissoes-filter-card__header">
          <div className="emissoes-busca">
            <div className="emissoes-busca__label">
              <span className="emissoes-busca__title">Selecionar Período</span>
              {periodoAtivoLabel ? (
                <span className="emissoes-busca__hint">
                  {periodoAtivoLabel}
                  {!activeQuickRange ? " (personalizado)" : ""}
                </span>
              ) : null}
            </div>
            <div className="emissoes-quick-range">
              {QUICK_RANGES.map((range) => (
                <button
                  key={range.key}
                  type="button"
                  className={`emissoes-quick-range__button ${
                    activeQuickRange === range.key ? "is-active" : ""
                  }`}
                  onClick={() => aplicarQuickRange(range.key)}
                  disabled={loading}
                >
                  {range.label}
                </button>
              ))}
            </div>
            <button
              type="button"
              className={`emissoes-filters-toggle${
                mostrarFiltros ? " is-open" : ""
              }`}
              onClick={() => setMostrarFiltros((v) => !v)}
              aria-expanded={mostrarFiltros}
            >
              <i className="tim-icons icon-settings-gear-63" />
              <span>Filtros</span>
              {filtrosAvancadosCount > 0 && (
                <span className="emissoes-filters-toggle__badge">
                  {filtrosAvancadosCount}
                </span>
              )}
            </button>
          </div>
        </CardHeader>

        {(mostrarFiltros || validationError || optionsError || error) ? (
          <CardBody>
          {mostrarFiltros ? (
            <div className="emissoes-filter-card__panel">
            <Row>
              <Col lg="3" md="6">
                <FormGroup>
                  <Label>De</Label>
                  <Input
                    type="date"
                    value={filtros.desde}
                    onChange={(e) => {
                      const valor = e.target.value;
                      setFiltros((current) => ({ ...current, desde: valor }));
                    }}
                    disabled={loading}
                  />
                </FormGroup>
              </Col>
              <Col lg="3" md="6">
                <FormGroup>
                  <Label>Até</Label>
                  <Input
                    type="date"
                    value={filtros.ate}
                    onChange={(e) => {
                      const valor = e.target.value;
                      setFiltros((current) => ({ ...current, ate: valor }));
                    }}
                    disabled={loading}
                  />
                </FormGroup>
              </Col>
              <Col lg="3" md="6">
                <FormGroup>
                  <Label>Modal</Label>
                  <Select
                    className="react-select info"
                    classNamePrefix="react-select"
                    options={filterOptions.modais}
                    value={filtros.modal}
                    onChange={(value) =>
                      setFiltros((current) => ({ ...current, modal: value || null }))
                    }
                    isDisabled={loading || loadingOptions}
                    isClearable
                    placeholder="Todos os modais"
                    noOptionsMessage={() => "Sem opções"}
                    styles={selectStyles}
                    menuPortalTarget={selectMenuPortalTarget}
                  />
                </FormGroup>
              </Col>
              <Col lg="3" md="6">
                <FormGroup>
                  <Label>UF de origem</Label>
                  <Select
                    className="react-select info"
                    classNamePrefix="react-select"
                    options={filterOptions.estados}
                    value={filtros.ufOrigem}
                    onChange={(value) =>
                      setFiltros((current) => ({ ...current, ufOrigem: value || null }))
                    }
                    isDisabled={loading || loadingOptions}
                    isClearable
                    placeholder="Todas"
                    noOptionsMessage={() => "Sem opções"}
                    styles={selectStyles}
                    menuPortalTarget={selectMenuPortalTarget}
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col lg="3" md="6">
                <FormGroup>
                  <Label>UF de destino</Label>
                  <Select
                    className="react-select info"
                    classNamePrefix="react-select"
                    options={filterOptions.estados}
                    value={filtros.ufDestino}
                    onChange={(value) =>
                      setFiltros((current) => ({ ...current, ufDestino: value || null }))
                    }
                    isDisabled={loading || loadingOptions}
                    isClearable
                    placeholder="Todas"
                    noOptionsMessage={() => "Sem opções"}
                    styles={selectStyles}
                    menuPortalTarget={selectMenuPortalTarget}
                  />
                </FormGroup>
              </Col>
              <Col lg="9" md="6" className="d-flex align-items-end">
                <div className="emissoes-filter-card__actions">
                  <Button
                    size="sm"
                    style={autopilotSecondaryButtonStyle}
                    onClick={consultar}
                    disabled={loading || Boolean(validationError)}
                  >
                    {loading ? <ClipLoader size={14} color="#fff" /> : "Aplicar filtros"}
                  </Button>
                  <Button
                    size="sm"
                    color="default"
                    outline
                    onClick={resetarFiltros}
                    disabled={loading}
                  >
                    Limpar
                  </Button>
                </div>
              </Col>
            </Row>
            </div>
          ) : null}

          {(validationError || optionsError || error) && (
            <div className="emissoes-feedback-stack">
              {validationError ? (
                <div className="emissoes-inline-alert emissoes-inline-alert--warning">
                  {validationError}
                </div>
              ) : null}
              {optionsError ? (
                <div className="emissoes-inline-alert emissoes-inline-alert--muted">
                  {optionsError}
                </div>
              ) : null}
              {error ? (
                <div className="emissoes-inline-alert emissoes-inline-alert--danger">
                  {error}
                </div>
              ) : null}
            </div>
          )}
          </CardBody>
        ) : null}
      </Card>

      {loading && !data ? (
        <Card className="card-everlog-shell emissoes-loading-card">
          <CardBody>
            <div className="emissoes-state-panel">
              <ClipLoader size={42} color="#1d8cf8" />
              <h4>Carregando indicador</h4>
              <p>Buscando CTes calculados, agregados por modal e ranking das maiores emissões.</p>
            </div>
          </CardBody>
        </Card>
      ) : null}

      {!loading && consultado && !hasResults ? (
        <EmptyPanel
          title="Nenhum CTe calculado foi encontrado"
          description="Ajuste o período, refine origem/destino ou revise a seleção de transportadora no cabeçalho. O dashboard só considera CTes com cálculo de emissão disponível."
          actions={
            <>
              <Button className="emissoes-button-primary" onClick={consultar}>
                Tentar novamente
              </Button>
              <Button className="emissoes-button-secondary" onClick={resetarFiltros}>
                Voltar ao recorte padrão
              </Button>
            </>
          }
        />
      ) : null}

      {hasResults ? (
        <>
          <SummaryOverviewCard kpis={data.kpis} />

          {equivalenciasEsg ? (
            <SectionCard
              eyebrow="Sustentabilidade"
              title="Equivalências"
              description="O que a pegada medida do período representa no dia a dia."
              actions={
                <Button
                  size="sm"
                  className="emissoes-button-primary emissoes-button-primary--sm"
                  disabled={cardEsgLoading}
                  onClick={baixarCardEsg}
                  title="Baixar card compartilhável (PNG)"
                >
                  {cardEsgLoading ? (
                    <ClipLoader size={10} color="#ffffff" />
                  ) : (
                    <>
                      <i className="tim-icons icon-image-02" /> Baixar card (PNG)
                    </>
                  )}
                </Button>
              }
            >
              <div className="emissoes-summary-grid">
                <SummaryMetricCard
                  label="Árvores por 1 ano"
                  tag="≈"
                  value={formatNumber(equivalenciasEsg.arvores, 0)}
                  helper={EQUIVALENCIA_LABELS.arvores}
                  iconClassName="tim-icons icon-planet"
                  tone="success"
                />
                <SummaryMetricCard
                  label="Carros por 1 ano"
                  tag="≈"
                  value={formatNumber(equivalenciasEsg.carros, 0)}
                  helper={EQUIVALENCIA_LABELS.carros}
                  iconClassName="tim-icons icon-delivery-fast"
                  tone="warning"
                />
                <SummaryMetricCard
                  label="Voos SP-Nova York"
                  tag="≈"
                  value={formatNumber(equivalenciasEsg.voos, 0)}
                  helper={EQUIVALENCIA_LABELS.voos}
                  iconClassName="tim-icons icon-spaceship"
                  tone="info"
                />
                <SummaryMetricCard
                  label="Litros de gasolina"
                  tag="≈"
                  value={formatNumber(equivalenciasEsg.gasolina, 0)}
                  helper={EQUIVALENCIA_LABELS.gasolina}
                  iconClassName="tim-icons icon-bulb-63"
                  tone="pink"
                />
              </div>
              {notaCoberturaEsg ? (
                <p
                  className="emissoes-section-card__description"
                  style={{ marginTop: 12, marginBottom: 0 }}
                >
                  {notaCoberturaEsg}
                </p>
              ) : null}
            </SectionCard>
          ) : null}

          <SectionCard
            eyebrow="Dashboard"
            title="Comparativos do período"
            description="Leitura agregada da emissão por modal e da evolução mensal do recorte."
          >
            <Row>
              <Col xl="6">
                <InsetPanel
                  title="Emissão por modal"
                  description="Comparativo em toneladas de CO₂ por modal."
                >
                  <div className="emissoes-chart-card__body">
                    {datasetModal ? (
                      <Bar
                        data={datasetModal}
                        options={buildChartOptions({
                          metricLabel: "t CO₂",
                          decimals: 3,
                        })}
                        height={250}
                      />
                    ) : (
                      <div className="emissoes-card-placeholder">Sem dados para este recorte.</div>
                    )}
                  </div>
                </InsetPanel>
              </Col>
              <Col xl="6">
                <InsetPanel
                  title="Evolução mensal"
                  description="Tendência do período em toneladas de CO₂."
                >
                  <div className="emissoes-chart-card__body">
                    {datasetLinha ? (
                      <Line
                        data={datasetLinha}
                        options={buildChartOptions({
                          metricLabel: "t CO₂",
                          decimals: 3,
                          fill: true,
                        })}
                        height={250}
                      />
                    ) : (
                      <div className="emissoes-card-placeholder">Sem dados para este recorte.</div>
                    )}
                  </div>
                </InsetPanel>
              </Col>
            </Row>
          </SectionCard>

          <SectionCard
            eyebrow="Ranking"
            title="Transportadoras com maior impacto"
            description="Comparativo entre as líderes em emissão total e o apoio tabular do mesmo ranking."
          >
            <Row>
              <Col xl="7">
                <InsetPanel
                  title="Top transportadoras"
                  description="Ranking por emissão total em toneladas de CO₂."
                >
                  <div className="emissoes-chart-card__body emissoes-chart-card__body--large">
                    {datasetTransportadoras ? (
                      <HorizontalBar
                        data={datasetTransportadoras}
                        options={buildChartOptions({
                          metricLabel: "t CO₂",
                          decimals: 3,
                          horizontal: true,
                        })}
                        height={320}
                      />
                    ) : (
                      <div className="emissoes-card-placeholder">Sem dados para este recorte.</div>
                    )}
                  </div>
                </InsetPanel>
              </Col>
              <Col xl="5">
                <InsetPanel
                  title="Detalhe do ranking"
                  description="Apoio tabular para leitura rápida das transportadoras líderes."
                >
                  <div className="emissoes-table-card__body emissoes-table-card__body--compact">
                    <Table size="sm" responsive className="emissoes-table">
                      <thead>
                        <tr>
                          <th>Transportadora</th>
                          <th className="text-right">CTes</th>
                          <th className="text-right">t CO₂</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(data.topTransportadoras || []).map((row) => (
                          <tr key={row.idTransportadora || row.nomeTransportadora}>
                            <td>{row.nomeTransportadora || "-"}</td>
                            <td className="text-right">{formatNumber(row.qtdCtes, 0)}</td>
                            <td className="text-right">
                              {formatNumber(Number(row.totalKgCO2 || 0) / 1000, 3)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </InsetPanel>
              </Col>
            </Row>
          </SectionCard>

          <SectionCard
            eyebrow="Relatório ESG"
            title="Relatório do período e CTes com maior emissão"
            description={tableSummary}
            actions={
              <>
                <Button
                  size="sm"
                  className="emissoes-button-primary emissoes-button-primary--sm"
                  disabled={relatorioEsgLoading}
                  onClick={gerarRelatorioEsg}
                  title="Baixar relatório ESG do período (PDF)"
                >
                  {relatorioEsgLoading ? (
                    <ClipLoader size={10} color="#ffffff" />
                  ) : (
                    <>
                      <i className="tim-icons icon-single-copy-04" /> Relatório PDF
                    </>
                  )}
                </Button>
                <Button
                  size="sm"
                  color="success"
                  disabled={excelLoading}
                  onClick={baixarExcel}
                  title="Baixar relatório do período em Excel (.xlsx)"
                >
                  {excelLoading ? (
                    <ClipLoader size={10} color="#ffffff" />
                  ) : (
                    <>
                      <i className="tim-icons icon-cloud-download-93" /> Excel
                    </>
                  )}
                </Button>
              </>
            }
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 10,
                marginBottom: 12,
              }}
            >
              {coberturaPctEsg ? (
                <span
                  className="emissoes-source-pill emissoes-source-pill--info"
                  title={notaCoberturaEsg}
                >
                  Cobertura {coberturaPctEsg}%
                </span>
              ) : null}
              <span className="emissoes-muted-text" style={{ marginBottom: 0 }}>
                Os totais do período (modal, transportadora, mês e equivalências) estão no PDF
                e no Excel. Use a tabela para buscar e abrir cada CT-e.
              </span>
            </div>
            <div style={{ display: "flex", gap: 8, marginBottom: 12, maxWidth: 520 }}>
              <Input
                bsSize="sm"
                placeholder="Buscar por nº do CTe ou transportadora"
                value={ctesBuscaInput}
                onChange={(e) => setCtesBuscaInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") aplicarBuscaCtes();
                }}
              />
              <Button
                size="sm"
                className="emissoes-button-primary emissoes-button-primary--sm"
                onClick={aplicarBuscaCtes}
                disabled={ctesLoading}
              >
                Buscar
              </Button>
              {ctesBusca ? (
                <Button
                  size="sm"
                  className="emissoes-button-secondary"
                  onClick={limparBuscaCtes}
                  disabled={ctesLoading}
                >
                  Limpar
                </Button>
              ) : null}
            </div>
            <div className="emissoes-table-card__body">
                <Table size="sm" responsive className="emissoes-table emissoes-table--wide">
                  <thead>
                    <tr>
                      <th>Documento</th>
                      <th>Data</th>
                      <th>Transportadora</th>
                      <th>Modal</th>
                      <th>Origem</th>
                      <th>Destino</th>
                      <th className="text-right">Distância (km)</th>
                      <th className="text-right">Peso (t)</th>
                      <th className="text-right">Emissão (kg CO₂)</th>
                      <th>Fonte</th>
                      <th className="text-center">Detalhes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ctesData.rows.length === 0 ? (
                      <tr>
                        <td colSpan={11} className="text-center emissoes-muted-text">
                          {ctesLoading
                            ? "Carregando CT-es..."
                            : "Nenhum CTe para o recorte/busca."}
                        </td>
                      </tr>
                    ) : null}
                    {ctesData.rows.map((row) => {
                      const sourceMeta = getDistanceSourceMeta(row.fonteDistancia);

                      return (
                        <tr key={row.idCte}>
                          <td>{row.numeroCte || row.idCte}</td>
                          <td>{formatDate(row.dataEmissao)}</td>
                          <td>{row.transportadora || "-"}</td>
                          <td>{row.modal || "-"}</td>
                          <td>{formatLocation(row.ufOrigem, row.cidadeOrigem)}</td>
                          <td>{formatLocation(row.ufDestino, row.cidadeDestino)}</td>
                          <td className="text-right">{formatMaybeNumber(row.distanciaKm, 2)}</td>
                          <td className="text-right">{formatMaybeNumber(row.pesoToneladas, 3)}</td>
                          <td className="text-right emissoes-table__metric">
                            {formatMaybeNumber(row.emissaoKgCO2, 2)}
                          </td>
                          <td>
                            <span
                              className={`emissoes-source-pill emissoes-source-pill--${sourceMeta.tone}`}
                              title={row.fonteDistancia || "-"}
                            >
                              {sourceMeta.label}
                            </span>
                          </td>
                          <td className="text-center">
                            <div className="emissoes-table__actions">
                              <Button
                                size="sm"
                                style={ROW_ACTION_BUTTON_STYLE}
                                disabled={detailLoadingId === row.idCte}
                                onClick={() => abrirDetalheCte(row.idCte)}
                                title="Ver detalhes na tela"
                              >
                                {detailLoadingId === row.idCte ? (
                                  <ClipLoader size={10} color="var(--theme-accent)" />
                                ) : (
                                  <>
                                    <i className="tim-icons icon-zoom-split" /> Ver
                                  </>
                                )}
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  {ctesData.rows.length > 0 ? (
                    <tfoot>
                      <tr className="emissoes-table__total-row">
                        <th colSpan={6}>Total desta página</th>
                        <th className="text-right">
                          {formatNumber(ctesPageTotais.distanciaKm, 2)}
                        </th>
                        <th className="text-right">
                          {formatNumber(ctesPageTotais.pesoToneladas, 3)}
                        </th>
                        <th className="text-right">
                          {formatNumber(ctesPageTotais.emissaoKgCO2, 2)}
                        </th>
                        <th />
                        <th />
                      </tr>
                    </tfoot>
                  ) : null}
                </Table>
            </div>
            {ctesData.total > 0 ? (
              <div className="emissoes-pagination">
                <span className="emissoes-pagination__info">
                  {`${formatNumber(ctesData.total, 0)} CTe(s)`} · página{" "}
                  {operacaoPaginaAtual} de {ctesTotalPaginas}
                </span>
                <div className="emissoes-pagination__controls">
                  <Input
                    type="select"
                    bsSize="sm"
                    className="emissoes-pagination__size"
                    value={operacaoPageSize}
                    onChange={(e) => {
                      setOperacaoPageSize(Number(e.target.value));
                      setOperacaoPage(1);
                    }}
                  >
                    {OPERACAO_PAGE_SIZES.map((s) => (
                      <option key={s} value={s}>
                        {s}/pág
                      </option>
                    ))}
                  </Input>
                  {renderOperacaoPager("Primeiro", 1, operacaoPaginaAtual <= 1)}
                  {renderOperacaoPager(
                    "Anterior",
                    operacaoPaginaAtual - 1,
                    operacaoPaginaAtual <= 1
                  )}
                  {renderOperacaoPager(
                    "Próximo",
                    operacaoPaginaAtual + 1,
                    operacaoPaginaAtual >= ctesTotalPaginas
                  )}
                  {renderOperacaoPager(
                    "Último",
                    ctesTotalPaginas,
                    operacaoPaginaAtual >= ctesTotalPaginas
                  )}
                </div>
              </div>
            ) : null}
          </SectionCard>
        </>
      ) : null}

      <DetalheEmissaoModal
        isOpen={detailModalOpen}
        onClose={fecharDetalheCte}
        loading={detailLoading}
        error={detailError}
        detalhe={detailData}
        onDownloadPdf={() => baixarRelatorioPdf(detailData?.idCte)}
        pdfLoading={pdfLoadingId === detailData?.idCte}
      />
    </div>
  );
};

export default EmissoesCarbonoIndicador;
